import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {DataSetMetricsGroupsState} from './dataSetMetricsGroups';
import {
  createDataSetMetricsGroupSuccess,
  deleteDataSetMetricsGroupSuccess,
  initializeDataSetMetricsGroups,
  initializeDataSetMetricsGroupsSuccess,
  loadDataSetMetricsGroups,
  loadDataSetMetricsGroupsSuccess,
  updateDataSetMetricsGroups,
  updateDataSetMetricsGroupsSuccess,
  updateDataSetMetricsGroupSuccess
} from './dataSetMetricsGroups.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {Serializer, Serializers} from '../../interfaces/serializer';
import {DataSetMetricsGroup} from '../../interfaces/metrics';
import {
  DatasetMetricsGroupComponentObject,
  DatasetMetricsGroupComponentObjects
} from '../../classes/dataset-metrics-group-component-object';
import {MetricComponentObjects} from '../../classes/metric-component-object';
import {Sort} from '../../libraries/sort';
import {DatasetDimensionsGroupComponentObject} from '../../classes/dataset-dimensions-group-component-object';
import {DataSetDimensionsGroup} from '../../interfaces/dimensions';

const _reducer: ActionReducer<DataSetMetricsGroupsState> = createReducer(
  { serialized: [], componentObjects: [], loading: false, loaded: false, initialized: false },
  on(loadDataSetMetricsGroups, (state: DataSetMetricsGroupsState): DataSetMetricsGroupsState => {
    return {
      ...state,
      loading: true,
      loaded: false
    };
  }),
  on(loadDataSetMetricsGroupsSuccess, (state: DataSetMetricsGroupsState, action: TypedAction<string> & { groups: Serializers<DataSetMetricsGroup> }): DataSetMetricsGroupsState => {
    return {
      ...state,
      serialized: action.groups,
      componentObjects: _sort(action.groups.map((group: Serializer<DataSetMetricsGroup>): DatasetMetricsGroupComponentObject => new DatasetMetricsGroupComponentObject(group))),
      loading: false,
      loaded: true
    };
  }),
  on(updateDataSetMetricsGroups, (state: DataSetMetricsGroupsState): DataSetMetricsGroupsState => {
    return {
      ...state,
      loading: true,
      loaded: false
    };
  }),
  on(updateDataSetMetricsGroupsSuccess, (state: DataSetMetricsGroupsState, action: TypedAction<string> & { groups: Serializers<DataSetMetricsGroup> }): DataSetMetricsGroupsState => {
    action.groups.forEach((dimension: Serializer<DataSetMetricsGroup>): void => {
      const componentObject: DatasetMetricsGroupComponentObject = state.componentObjects.find((componentObject: DatasetMetricsGroupComponentObject): boolean => componentObject.payload.id === dimension.id);

      if (componentObject) {
        componentObject.update(dimension);
      } else {
        state.componentObjects.push(new DatasetMetricsGroupComponentObject(dimension));
      }
    });

    state.componentObjects = state.componentObjects.filter((group: DatasetMetricsGroupComponentObject): boolean => action.groups.includes(group.payload as Serializer<DataSetMetricsGroup>));

    return {
      ...state,
      serialized: action.groups,
      componentObjects: _sort(state.componentObjects),
      loading: false,
      loaded: true
    };
  }),
  on(createDataSetMetricsGroupSuccess, (state: DataSetMetricsGroupsState, action: TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }): DataSetMetricsGroupsState => {
    return {
      ...state,
      serialized: [
        ...state.serialized,
        action.group
      ],
      componentObjects: _sort([
        ...state.componentObjects,
        new DatasetMetricsGroupComponentObject(action.group)
      ])
    };
  }),
  on(updateDataSetMetricsGroupSuccess, (state: DataSetMetricsGroupsState, action: TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }): DataSetMetricsGroupsState => {
    state.componentObjects.find((group: DatasetMetricsGroupComponentObject): boolean => group.payload.id === action.group.id).update(action.group);

    return {
      ...state,
      serialized: state.serialized.map((group: Serializer<DataSetMetricsGroup>): Serializer<DataSetMetricsGroup> => group.id === action.group.id && action.group || group),
      componentObjects: _sort(state.componentObjects)
    };
  }),
  on(deleteDataSetMetricsGroupSuccess, (state: DataSetMetricsGroupsState, action: TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }): DataSetMetricsGroupsState => {
    return {
      ...state,
      serialized: state.serialized.filter((group: Serializer<DataSetMetricsGroup>): boolean => group.id !== action.group.id),
      componentObjects: state.componentObjects.filter((group: DatasetMetricsGroupComponentObject): boolean => group.payload.id !== action.group.id)
    };
  }),
  on(initializeDataSetMetricsGroups, (state: DataSetMetricsGroupsState): DataSetMetricsGroupsState => {
    return {
      ...state,
      initialized: false
    };
  }),
  on(initializeDataSetMetricsGroupsSuccess, (state: DataSetMetricsGroupsState, action: TypedAction<string> & { datasetMetrics: MetricComponentObjects }): DataSetMetricsGroupsState => {
    state.componentObjects.forEach((group: DatasetMetricsGroupComponentObject): void => {
      group.init(action.datasetMetrics);
    });

    return {
      ...state,
      initialized: true
    };
  })
);

export const dataSetMetricsGroupsReducer: ActionReducer<DataSetMetricsGroupsState> = (state: DataSetMetricsGroupsState, action: Action) => {
  return _reducer(state, action);
}

function _sort(groups: DatasetMetricsGroupComponentObjects): DatasetMetricsGroupComponentObjects {
  return [
    groups.find((group: DatasetMetricsGroupComponentObject): boolean => group.payload.attributes.slug === 'attribution-offline'),
    ...groups
      .filter((group: DatasetMetricsGroupComponentObject): boolean => !['attribution-offline'].includes(group.payload.attributes.slug))
      .sort((a: DatasetMetricsGroupComponentObject, b: DatasetMetricsGroupComponentObject): number => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name))
  ];
}
